/**
 * Excel Entity Helpers for Option Entities
 * Shared utilities for detecting and extracting data from OptLab option entities
 */

/* global Excel */
/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Extract basicValue from nested property shapes
 * Handles common patterns:
 * - { basicValue: "..." }
 * - { value: { basicValue: "..." } }
 * - Direct string value
 */
export function tryGetBasicValue(p: any): string | undefined {
  if (p === undefined || p === null) {
    return undefined;
  }

  // Direct string
  if (typeof p === "string") {
    return p;
  }

  // { basicValue: "..." }
  if (typeof p === "object" && "basicValue" in p) {
    const value = p.basicValue;
    if (typeof value === "string") {
      return value;
    }
  }

  // { value: { basicValue: "..." } }
  if (typeof p === "object" && "value" in p && typeof p.value === "object") {
    if ("basicValue" in p.value && typeof p.value.basicValue === "string") {
      return p.value.basicValue;
    }
  }

  return undefined;
}

/**
 * Check if a cell value is an OptLab option entity
 * Uses shape-based detection (tolerant of different Excel API versions)
 */
export function isOptLabOptionEntityCellValue(v: any): boolean {
  if (!v || typeof v !== "object") {
    return false;
  }

  // Check if entity-like: multiple patterns for tolerance
  // Safe check for Excel API (may not be available in test environments)
  let excelEntityType: any = undefined;
  try {
    if (typeof Excel !== "undefined" && Excel?.CellValueType?.entity) {
      excelEntityType = Excel.CellValueType.entity;
    }
  } catch {
    // Excel not available (e.g., in test environment)
  }

  const isEntityLike =
    v.type === "Entity" ||
    (excelEntityType !== undefined && v.type === excelEntityType) ||
    (v.entity?.type && v.properties) ||
    (v.type && v.properties && typeof v.properties === "object");

  if (!isEntityLike) {
    return false;
  }

  // Assert "our entity": Check for OCC symbol property (stable marker)
  // This is more reliable than checking entity.type which may not be set
  // or may differ across Excel API versions
  const properties = v.properties || v.entity?.properties;
  if (!properties || typeof properties !== "object") {
    return false;
  }

  // Check for OCC symbol in any of the expected property keys
  const occSymbol = getOccSymbolFromEntityCellValue(v);
  if (occSymbol) {
    return true;
  }

  // Fallback: Check provider description as marker
  const provider = v.provider || v.entity?.provider;
  if (provider?.description === "Market data provided by Massive") {
    return true;
  }

  return false;
}

/**
 * Extract OCC symbol from an OptLab option entity cell value
 * Tries multiple property key formats in order of preference
 */
export function getOccSymbolFromEntityCellValue(v: any): string | undefined {
  if (!v || typeof v !== "object") {
    return undefined;
  }

  const properties = v.properties || v.entity?.properties;
  if (!properties || typeof properties !== "object") {
    return undefined;
  }

  // Try in order of preference:
  // 1. Schema key (preferred) - may not be present if only display name is stored
  if (properties["occ_symbol"]) {
    const value = tryGetBasicValue(properties["occ_symbol"]);
    if (value) {
      return value;
    }
  }

  // 2. Contract symbol variants (if renamed)
  if (properties["contractSymbol"]) {
    const value = tryGetBasicValue(properties["contractSymbol"]);
    if (value) {
      return value;
    }
  }
  if (properties["contract_symbol"]) {
    const value = tryGetBasicValue(properties["contract_symbol"]);
    if (value) {
      return value;
    }
  }

  // 3. Display name (fallback) - this is what formatPropertyDisplayName("occ_symbol") produces
  if (properties["OCC Symbol"]) {
    const value = tryGetBasicValue(properties["OCC Symbol"]);
    if (value) {
      return value;
    }
  }

  return undefined;
}
